import { useLazyQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { SuggestionOption } from '@modules/common/components/MultiColumnAutoComplete/MultiColumnAutoCompleteExample/types';
import { useShouldRemoveFileUploadPrefix } from '@modules/common/hooks/useShouldRemoveFileUploadPrefix';
import { ListQueryBuilderGroupsInput, Query } from 'generated/graphql';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { FETCH_QUERY_BUILDER_GROUPS_GQL } from './constants';
import { getGroupOptions } from './helpers/getGroupOptions';

interface useFetchQueryBuilderGroupOptionsParams {
  searchTerm: string;
  limitOptions?: number;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export function useFetchQueryBuilderGroupOptions({
  searchTerm,
  limitOptions = 3,
  fetchPolicy = 'cache-and-network',
}: useFetchQueryBuilderGroupOptionsParams) {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const [rows, setRows] = useState<string[][]>([]);
  const [options, setOptions] = useState<SuggestionOption[]>([]);
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const [fetchQueryBuilderGroups, resp] = useLazyQuery<
    Query,
    ListQueryBuilderGroupsInput
  >(FETCH_QUERY_BUILDER_GROUPS_GQL, {
    variables: {
      tenantID,
      searchTerm,
      limitOptions,
    },
    fetchPolicy,
    nextFetchPolicy: 'cache-first',
    onCompleted(data) {
      const { options, rows } = getGroupOptions({
        queryBuilderGroup: data.listQueryBuilderGroups?.groups,
        shouldRemoveFileUploadPrefix,
      });

      setOptions(options);
      setRows(rows);
    },
  });

  const deps = {
    options,
    searchTerm,
    tenantID,
    limitOptions,
    fetchQueryBuilderGroups,
  };
  const depsRef = useRef(deps);
  depsRef.current = { ...deps };

  const fetchQueryBuilderGroupOptions = useCallback((searchTerm?: string) => {
    depsRef.current.fetchQueryBuilderGroups({
      variables: {
        tenantID: depsRef.current.tenantID,
        limitOptions: depsRef.current.limitOptions,
        searchTerm,
      },
    });
  }, []);

  const filteredOptions = useMemo(() => {
    const updatedOptions = options.filter((option) => {
      return option.groupID !== 'entitySentiment';
    });

    // clear the value of the text search option to avoid text search hangover
    // Other solution is to kill the cache, which is not optimal
    // https://linear.app/enterpret/issue/ENG-12559/text-search-hangover
    if (!depsRef.current.searchTerm) {
      return [...updatedOptions].map((option) =>
        option.field.field === 'search'
          ? { ...option, value: undefined }
          : option,
      );
    }

    return updatedOptions;
  }, [options]);

  return [
    fetchQueryBuilderGroupOptions,
    {
      rows,
      options: filteredOptions,
      isLoading: resp.loading,
    },
  ] as const;
}
